import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ArrowLeft, Edit, LucideAngularModule, Save, Trash, X, CheckCircle, Plus } from 'lucide-angular';
import { ActivatedRoute, Router } from '@angular/router';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AbstractEntityEditComponent } from '@app/shared/abstract/abstract-entity-edit.component';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { TextInputComponent } from '@app/partials/form/text-input/text-input.component';
import { UserModel } from '@app/users/shared/user.model';
import { ContactService, ExistingContactMatch } from '@app/contacts/shared/contact.service';
import { AuthService } from '@app/auth/auth.service';
import { ArrayInputComponent } from '@app/partials/form/array-input/array-input.component';
import { ModalComponent } from '@app/partials/modal/modal.component';
import { Editor, NgxEditorModule, Toolbar } from 'ngx-editor';
import { EventService } from '@app/events/shared/event.service';
import { RouteParams } from '@app/shared/abstract/route-params';
import { map, Observable, forkJoin, Subject, merge, debounceTime, distinctUntilChanged, switchMap, takeUntil, of, catchError } from 'rxjs';
import { SelectComponent } from '@app/partials/form/select/select.component';
import { SearchableSelectComponent } from '@app/partials/form/searchable-select/searchable-select.component';
import { DatePipe } from '@angular/common';
import { FeatureToggleService } from '@app/feature-toggles/feature-toggle.service';
import { ContactJourneyComponent } from '@app/contacts/edit/contact-journey.component';
import { ContactJourneySummaryComponent } from '@app/contacts/edit/contact-journey-summary.component';
import { ContactSentEmailsComponent } from '@app/contacts/edit/contact-sent-emails.component';
import { ContactEmailAddressesComponent } from '@app/contacts/edit/contact-email-addresses.component';
import { ContactConsentHistoryComponent } from '@app/contacts/edit/contact-consent-history.component';
import { ContactShowRoundsComponent } from '@app/contacts/edit/contact-show-rounds.component';
import { ContactScheduledEmailsComponent } from '@app/contacts/edit/contact-scheduled-emails.component';
import { ContactQaToolsComponent } from '@app/contacts/edit/contact-qa-tools.component';
import { HelpPanelComponent } from '@app/partials/help-panel/help-panel.component';
import { EventRoleNameService } from '@app/events/shared/event-role-name.service';
import { CheckboxComponent } from '@app/partials/form/checkbox/checkbox.component';
import {
  BookNewViewingModalComponent,
  BookNewViewingPayload,
} from '@app/board/view/book-new-viewing-modal.component';
import { BookableViewingContact } from '@app/events/shared/event.service';
import { CustomerHomeJourney } from '@app/customer/shared/customer-home.model';
import { ContactShowRound } from '@app/contacts/shared/contact-show-round.model';
import { NotificationService } from '@app/shared/notification/notification.service';

@Component({
  selector: 'app-edit',
  imports: [
    ButtonComponent,
    ReactiveFormsModule,
    TextInputComponent,
    ArrayInputComponent,
    ModalComponent,
    LucideAngularModule,
    NgxEditorModule,
    SelectComponent,
    SearchableSelectComponent,
    DatePipe,
    ContactJourneyComponent,
    ContactJourneySummaryComponent,
    ContactSentEmailsComponent,
    ContactEmailAddressesComponent,
    ContactConsentHistoryComponent,
    ContactShowRoundsComponent,
    ContactScheduledEmailsComponent,
    ContactQaToolsComponent,
    HelpPanelComponent,
    CheckboxComponent,
    BookNewViewingModalComponent,
  ],
  templateUrl: './contact-edit.component.html',
})
export class ContactEditComponent extends AbstractEntityEditComponent implements OnInit, OnDestroy {
  protected processing = false;
  protected validationErrors: string[] = [];
  protected user: UserModel;
  protected readonly SaveIcon = Save;
  protected readonly TrashIcon = Trash;
  protected readonly EditIcon = Edit;
  protected readonly XIcon = X;
  protected readonly CheckCircleIcon = CheckCircle;
  protected readonly PlusIcon = Plus;
  protected readonly ArrowLeftIcon = ArrowLeft;

  // TVG-120: roles needed to see the Create Event button. Mirrors
  // EventPolicy::create — admin + manager only.
  protected canCreateEvents = false;

  // TVG-136: read View vs Edit. Both modes share one layout; View simply turns
  // editing off (form disabled, action buttons hidden). Set from the route's
  // `readOnly` data flag so the `/contacts/:id` view route and the
  // `/contacts/:id/edit` edit route render the same component.
  protected readonly readOnly: boolean;

  // TVG-136: admin/manager can switch from View into Edit. Mirrors the contact
  // edit route's role gate so we never offer an Edit button that 403s.
  protected canEditContact = false;

  /**
   * TVG-360: editing now happens on the contact page itself rather than on a
   * separate edit route — the contact block's fields are read only until Edit
   * is pressed. The `/contacts/:id/edit` route still exists and simply lands
   * already editing, so every existing link keeps working.
   */
  protected editing: boolean;

  /** TVG-360: Book a show round, from this page rather than the Deals board. */
  protected bookShowRoundOpen = false;
  protected bookShowRoundSubmitting = false;
  protected bookShowRoundLoading = false;
  protected bookableContacts: BookableViewingContact[] = [];

  /**
   * TVG-360 section eleven (a and b): what the summary block above show rounds
   * reads from. Both are handed up by the panels further down the page as they
   * load, so the journey and the viewings line always match the lists below
   * and neither is fetched a second time.
   */
  protected contactJourney: CustomerHomeJourney | null = null;
  protected contactShowRounds: ContactShowRound[] = [];

  @ViewChild(BookNewViewingModalComponent) private bookShowRoundModal?: BookNewViewingModalComponent;
  @ViewChild(ContactShowRoundsComponent) private showRoundsPanel?: ContactShowRoundsComponent;
  @ViewChild(ContactJourneyComponent) private journeyPanel?: ContactJourneyComponent;
  /** TVG-342: re-read when an extra email address files Not filed replies here. */
  @ViewChild(ContactSentEmailsComponent) protected sentEmailsPanel?: ContactSentEmailsComponent;

  protected noteForm!: FormGroup<{
    note: FormControl<string | null>;
  }>;

  protected processingNote = false;
  protected showNoteForm = false;
  protected editNoteId: number | null = null;

  protected isDeleteModalOpen = false;
  protected deleteNotesId: number | null = null;

  // TVG-343: with the communications toggle on the panel is the contact's
  // messages, both what the platform sent and what staff wrote, and carries a
  // Send an email button. With it off it is the read-only TVG-313 list again.
  protected get communicationsEnabled(): boolean {
    return this.featureToggleService.isEnabled('communications');
  }

  // TVG-120: Create Event button only shows when the toggle is on AND the
  // user can create events. The button navigates to /events/create with
  // pre-populated query params (contact, status=Opportunity).
  protected get crmSalesFlowEnabled(): boolean {
    return this.featureToggleService.isEnabled('crm-sales-flow');
  }

  protected get showCreateEventButton(): boolean {
    return this.crmSalesFlowEnabled && this.canCreateEvents && !!this.user?.id && !this.readOnly;
  }

  // TVG-318: the Add Contact form asks for a name and an email only, and looks
  // for an existing contact before it saves.
  protected existingMatches: ExistingContactMatch[] = [];
  protected searchingExisting = false;
  private readonly destroy$ = new Subject<void>();

  protected get isCreating(): boolean {
    return !this.user?.id;
  }

  /**
   * An existing contact already owns this email address, so saving would only
   * fail validation — staff should open that contact instead.
   */
  protected get duplicateEmailMatch(): ExistingContactMatch | undefined {
    return this.existingMatches.find((match) => match.exact_email_match);
  }

  protected initialEventChoices: { value: any; label: string }[] = [];
  protected eventSearchFunction: (term: string, page?: number) => Observable<{ options: { value: any; label: string }[], hasMore: boolean }>;
  protected contactStatusChoices = [
    { label: 'Opportunity', value: 'Opportunity' },
    { label: 'Provisional', value: 'Provisional' },
    { label: 'Confirmed', value: 'Confirmed' },
    { label: 'Cancelled', value: 'Cancelled' },
    { label: 'Closed', value: 'Closed' },
  ];

  /**
   * TVG-312: lead sources come from the admin-managed list rather than a
   * hardcoded set. Only active sources are offered; a contact already holding a
   * retired source keeps it (added back below) so editing the record does not
   * silently rewrite its history.
   */
  protected leadSourceChoices: { label: string; value: string }[] = [];

  editor?: Editor;
  toolbar: Toolbar = [
    ['bold', 'italic'],
    ['underline', 'strike'],
    ['ordered_list', 'bullet_list', 'link'],
    ['text_color', 'background_color'],
    [{ heading: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] }],
    ['align_left', 'align_center', 'align_right', 'align_justify'],
    ['undo', 'redo'],
  ];

  form = new FormGroup({
    name: new FormControl('', [Validators.required, Validators.minLength(3), Validators.maxLength(50)]),
    email: new FormControl('', [Validators.required]),
    // TVG-322: the second email was never used, so it is gone from the contact
    // pages and from everything they save.
    password: new FormControl('', [Validators.minLength(6), Validators.maxLength(50)]),
    address: new FormControl('', [Validators.maxLength(255)]), // Kept for backward compatibility
    address_line_1: new FormControl('', [Validators.maxLength(255)]),
    address_line_2: new FormControl<string | null>(null, [Validators.maxLength(255)]),
    city: new FormControl('', [Validators.maxLength(100)]),
    postcode: new FormControl('', [Validators.maxLength(20)]),
    country: new FormControl('', [Validators.maxLength(100)]),
    phone_numbers: new FormArray<FormControl<string>>([]),
    // TVG-315: the contact's three consent permissions. Email and text start
    // on, marketing starts off — the same defaults the backend applies, so a
    // new contact created here matches one created anywhere else.
    email_permission: new FormControl<boolean>(true, { nonNullable: true }),
    text_permission: new FormControl<boolean>(true, { nonNullable: true }),
    marketing_permission: new FormControl<boolean>(false, { nonNullable: true }),
    // TVG-360: corporate client, and the company name ticking it turns on.
    is_corporate: new FormControl<boolean>(false, { nonNullable: true }),
    company_name: new FormControl<string | null>(null, [Validators.maxLength(255)]),
    event_ids: new FormControl<number[]>([]),
    contact_status: new FormControl<'Opportunity' | 'Provisional' | 'Confirmed' | 'Cancelled' | 'Closed'>(
      'Opportunity',
      [Validators.required],
    ),
    // TVG-312: lead source, picked from the admin-managed list.
    source: new FormControl<string | null>(null),
    source_other: new FormControl<string | null>(null, [Validators.maxLength(255)]),
  });

  /** TVG-312: free-text detail only applies to the catch-all "Other" source. */
  protected get sourceIsOther(): boolean {
    return this.form.controls.source.value === 'Other';
  }

  /**
   * TVG-360: the company name belongs to a corporate client only. Ticking the
   * box brings the field to life; clearing the box empties it again, so a
   * contact that is no longer corporate keeps no company on file.
   */
  protected get isCorporateClient(): boolean {
    return this.form.controls.is_corporate.value === true;
  }

  /**
   * TVG-344: the saved permissions and numbers in one string. When a save
   * changes it, the consent history and its text check reload.
   */
  protected get consentStateKey(): string {
    return [
      this.user?.email_permission,
      this.user?.text_permission,
      this.user?.marketing_permission,
      (this.user?.phone_numbers ?? []).join(','),
    ].join('|');
  }

  constructor(
    private contactsService: ContactService,
    private route: ActivatedRoute,
    private eventsService: EventService,
    private router: Router,
    private featureToggleService: FeatureToggleService,
    private authService: AuthService,
    private eventRoleNameService: EventRoleNameService,
    private notificationService: NotificationService,
  ) {
    super();

    // TVG-136: read View vs Edit. The `/contacts/:id` route sets readOnly so
    // the page renders the same layout with editing turned off.
    this.readOnly = this.route.snapshot.data['readOnly'] === true;

    // Set up event search function for lazy loading using optimized choices endpoint
    this.eventSearchFunction = (term: string, page: number = 1) => {
      return this.eventsService.getChoices(term, page, 20);
    };

    if (this.route.snapshot.data['user']) {
      // Load initial events. TVG-318: the Add Contact form no longer offers the
      // events picker, so a new contact does not need the choices.
      this.loadInitialEvents();
      this.user = this.route.snapshot.data['user'];
      if (!this.user.notes) {
        this.user.notes = [];
      }
      this.form.get('event_ids')?.setValue(this.user.event_ids || []);
      this.form.patchValue(this.user);
      if (this.user.phone_numbers?.length) {
        this.user.phone_numbers.forEach((phone) => {
          (this.form.get('phone_numbers') as FormArray).push(new FormControl(phone));
        });
      }
      // Password is optional for existing contacts
      this.form.get('password')?.setValue('');
    } else {
      this.user = { notes: [] } as unknown as UserModel;
      this.form.patchValue(this.user);
      // TVG-318: a new contact is created from a name and an email only. The
      // password column is nullable and contacts set their own on first sign-in,
      // so the form no longer asks for one up front.
    }

    // TVG-136: in View mode the whole form is disabled after it has been
    // populated, so every field renders read-only. The text inputs also
    // receive [disabled]="readOnly" so their own enable/disable on init keeps
    // them off; selects and the phone array honour the disabled control.
    if (this.readOnly) {
      this.form.disable();
    }

    // TVG-360: the view route opens read only and waits for Edit; the edit and
    // create routes open ready to type in, as they always have.
    this.editing = !this.readOnly;
  }

  /**
   * TVG-360: turn the contact block's fields on in place. Previously this
   * navigated to `/contacts/:id/edit`; editing now happens here, so the page
   * keeps its scroll position and everything open on it stays open.
   */
  protected startEditing(): void {
    if (!this.canEditContact || this.editing) {
      return;
    }

    this.editing = true;
    this.form.enable();
    // A blank password means "leave it alone", the same as the edit route.
    this.form.controls.password.setValue('');
    this.form.markAsPristine();
  }

  /** The contact's events, read as chips while the block is read only. */
  protected get eventChips(): string[] {
    return (this.user?.events ?? []).map((event) => {
      const date = event.start_date
        ? new Date(event.start_date).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })
        : null;

      return [date, event.venue_name || event.name].filter((part) => !!part).join(', ');
    });
  }

  // Book a show round -------------------------------------------------------

  /**
   * TVG-360: open the booking dialog with this contact already chosen, and —
   * when the request came from an expanded event — that event chosen too, so
   * staff only have the date left to set.
   */
  protected openBookShowRound(eventId?: number): void {
    if (!this.user?.id) {
      return;
    }

    this.bookShowRoundOpen = true;
    this.bookShowRoundSubmitting = false;
    this.bookShowRoundLoading = true;
    this.bookShowRoundModal?.reset();

    this.eventsService.bookableViewingContacts(this.user.email ?? '').subscribe({
      next: (response) => {
        this.bookableContacts = response.data ?? [];
        this.bookShowRoundLoading = false;

        const contact = this.bookableContacts.find((candidate) => candidate.id === this.user.id);
        if (!contact) {
          // Nothing bookable: a viewing needs an event, and this contact has
          // none that can take one.
          this.bookShowRoundOpen = false;
          this.notificationService.error(
            'This contact has no event a viewing can be booked on yet. Create an event first.',
          );
          return;
        }

        this.bookShowRoundModal?.startWithContact(contact, eventId ?? null);
      },
      error: () => {
        this.bookShowRoundLoading = false;
        this.bookShowRoundOpen = false;
        this.notificationService.error('Unable to load this contact’s events');
      },
    });
  }

  protected closeBookShowRound(): void {
    this.bookShowRoundOpen = false;
    this.bookShowRoundSubmitting = false;
    this.bookShowRoundModal?.reset();
  }

  protected submitBookShowRound(payload: BookNewViewingPayload): void {
    if (this.bookShowRoundSubmitting) {
      return;
    }

    this.bookShowRoundSubmitting = true;
    this.eventsService
      .bookViewing(payload.event_id, payload.show_round_date, payload.show_round_time, payload.show_around_name_id)
      .subscribe({
        next: () => {
          this.closeBookShowRound();
          this.notificationService.success('Viewing booked');
          this.showRoundsPanel?.reload();
          this.journeyPanel?.reload();
        },
        error: () => {
          this.bookShowRoundSubmitting = false;
          this.notificationService.error('Unable to book the viewing');
        },
      });
  }

  ngOnInit(): void {
    this.editor = new Editor({
      history: true,
      keyboardShortcuts: true,
    });

    this.noteForm = new FormGroup({
      note: new FormControl('', {
        validators: [Validators.required, Validators.maxLength(1000)],
      }),
    });

    this.watchCorporateClient();

    // TVG-318: while staff type a name and an email on the Add Contact form,
    // look for a contact that already exists so they can open it instead of
    // creating a duplicate.
    if (this.isCreating) {
      this.watchForExistingContacts();
    }

    // TVG-120: resolve role gate for the Create Event button.
    this.authService.getUser().subscribe({
      next: (user) => {
        const roles = (user?.roles ?? []).map((role: any) => {
          if (typeof role === 'string') {
            return role.toLowerCase();
          }
          if (role && typeof role.name === 'string') {
            return role.name.toLowerCase();
          }
          return '';
        });
        // TVG-352: raising an event is for admins and managers.
        this.canCreateEvents = roles.includes('admin') || roles.includes('manager');
        // TVG-136: same gate as the contact edit route — only admin/manager
        // get the "Edit" button when viewing a contact read-only.
        this.canEditContact = roles.includes('admin') || roles.includes('manager');
      },
      error: () => {
        this.canCreateEvents = false;
        this.canEditContact = false;
      },
    });

    // TVG-322: the Source dropdown is offered on the Add Contact form too, so
    // the choices are needed whether or not the contact exists yet.
    this.loadLeadSourceChoices();
  }

  /**
   * TVG-360: keep the company name in step with the corporate client box —
   * required while it is ticked, cleared when it is not. TVG-322 offers the
   * same box on the Add Contact form, so this holds whether or not the contact
   * exists yet.
   */
  private watchCorporateClient(): void {
    this.applyCorporateRules(false);
    this.form.controls.is_corporate.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.applyCorporateRules(true));
  }

  /**
   * TVG-360: a company name is only asked for, and only kept, while the
   * corporate client box is ticked. `clearOnUntick` is false on first run so
   * loading a contact never wipes what is already stored.
   */
  private applyCorporateRules(clearOnUntick: boolean): void {
    const companyName = this.form.controls.company_name;

    if (this.isCorporateClient) {
      companyName.setValidators([Validators.required, Validators.maxLength(255)]);
    } else {
      companyName.setValidators([Validators.maxLength(255)]);
      if (clearOnUntick) {
        companyName.setValue(null);
      }
    }

    companyName.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * TVG-312: populate the Source dropdown from the admin-managed lead source
   * list. A retired source already on this contact is kept as an option so the
   * past value stays visible and survives a save of the other fields.
   */
  private loadLeadSourceChoices(): void {
    this.eventRoleNameService.getByRole('lead_source').subscribe({
      next: (res) => {
        const choices = res.data.map((entry) => ({ label: entry.name, value: entry.name }));
        const current = this.user?.source;
        if (current && !choices.some((choice) => choice.value === current)) {
          choices.push({ label: `${current} (retired)`, value: current });
        }
        this.leadSourceChoices = choices;
      },
      error: () => {
        this.leadSourceChoices = [];
      },
    });
  }

  // TVG-236: when this contact was opened from the Sales Dashboard worklist, the
  // active filter state travels along as `sd_*` query params. Offer a link that
  // returns to the dashboard with those filters intact rather than a reset view.
  protected get showBackToDashboard(): boolean {
    return this.readOnly && this.route.snapshot.queryParams['returnTo'] === 'sales-dashboard';
  }

  protected backToDashboard(): void {
    const filters = { ...this.route.snapshot.queryParams };
    delete filters['returnTo'];
    void this.router.navigate(['/sales-dashboard'], { queryParams: filters });
  }

  ngOnDestroy(): void {
    this.editor?.destroy();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * TVG-318: search existing contacts as the name and email are typed. The
   * backend ignores terms shorter than three characters, so an empty form makes
   * no noise.
   */
  private watchForExistingContacts(): void {
    const name = this.form.controls.name;
    const email = this.form.controls.email;

    merge(name.valueChanges, email.valueChanges)
      .pipe(
        debounceTime(400),
        map(() => `${(name.value ?? '').trim()}|${(email.value ?? '').trim()}`),
        distinctUntilChanged(),
        switchMap((term) => {
          const [typedName, typedEmail] = term.split('|');

          if (typedName.length < 3 && typedEmail.length < 3) {
            return of({ data: [] as ExistingContactMatch[] });
          }

          this.searchingExisting = true;

          return this.contactsService.searchExisting(typedName, typedEmail).pipe(
            catchError(() => of({ data: [] as ExistingContactMatch[] })),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((response) => {
        this.searchingExisting = false;
        this.existingMatches = response.data ?? [];
      });
  }

  // TVG-318: open the existing contact the search turned up rather than
  // creating a second record for the same person.
  protected openExistingContact(contactId: number): void {
    void this.router.navigate(['/contacts', contactId, 'edit']);
  }

  save() {
    this.performSave(false);
  }

  saveAndContinue() {
    this.performSave(true);
  }

  private performSave(continueEditing: boolean) {
    if (this.form.invalid) {
      return;
    }

    // TVG-318: an existing contact already owns this email — open it rather
    // than create a duplicate.
    if (this.duplicateEmailMatch) {
      return;
    }

    this.processing = true;
    const wasNewContact = !this.user?.id;

    // TVG-318/TVG-322: name and email are still the only fields a new contact
    // has to have. Alongside them the form now offers an address, a lead source
    // and the corporate tick box, so staff can record what they already know at
    // the point of creation rather than saving twice.
    const data = wasNewContact
      ? {
          name: this.form.get('name')?.value,
          email: this.form.get('email')?.value,
          address_line_1: this.form.get('address_line_1')?.value || null,
          source: this.form.get('source')?.value || null,
          source_other: this.sourceIsOther ? this.form.get('source_other')?.value || null : null,
          is_corporate: this.form.get('is_corporate')?.value,
          company_name: this.isCorporateClient ? this.form.get('company_name')?.value || null : null,
        }
      : {
          id: this.user.id,
          name: this.form.get('name')?.value,
          email: this.form.get('email')?.value,
          address: this.form.get('address')?.value, // Kept for backward compatibility
          address_line_1: this.form.get('address_line_1')?.value,
          address_line_2: this.form.get('address_line_2')?.value,
          city: this.form.get('city')?.value,
          postcode: this.form.get('postcode')?.value,
          country: this.form.get('country')?.value,
          phone_numbers: this.form.get('phone_numbers')?.value,
          // TVG-315: consent permissions travel with every save of an existing
          // contact, so a staff edit on the contact page is what sets them. A
          // brand-new contact (name + email only, TVG-318) takes the backend
          // defaults instead — email and text on, marketing off.
          email_permission: this.form.get('email_permission')?.value,
          text_permission: this.form.get('text_permission')?.value,
          marketing_permission: this.form.get('marketing_permission')?.value,
          // TVG-360: the company name only travels with a corporate client.
          is_corporate: this.form.get('is_corporate')?.value,
          company_name: this.isCorporateClient ? this.form.get('company_name')?.value || null : null,
          password: this.form.get('password')?.value || null,
          event_ids: this.form.get('event_ids')?.value || [],
          contact_status: this.form.get('contact_status')?.value,
          // TVG-312: free-text detail is only meaningful for the "Other" source.
          source: this.form.get('source')?.value || null,
          source_other: this.sourceIsOther ? this.form.get('source_other')?.value || null : null,
        };

    this.contactsService.save(data as unknown as UserModel).subscribe({
      next: (res) => {
        this.processing = false;
        this.user = res.data;
        if (!this.user.notes) {
          this.user.notes = [];
        }
        this.form.patchValue(this.user);
        this.form.markAsPristine();

        if (continueEditing) {
          // For saveAndContinue, stay on edit page
          if (wasNewContact && res.data?.id) {
            this.router.navigate(['/contacts', res.data.id, 'edit']);
          } else if (this.readOnly) {
            // TVG-360: saved from the contact block's own Save button on the
            // view page — put the fields back to read only, where they started.
            this.editing = false;
            this.form.disable();
          }
          // If editing existing contact, just stay on current page (no navigation)
        } else if (wasNewContact && res.data?.id) {
          // TVG-318: the contact was created from a name and an email only —
          // land on its page so the remaining details can be added there.
          this.router.navigate(['/contacts', res.data.id, 'edit']);
        } else {
          // For regular save, go to index page
          this.router.navigate(['/contacts']);
        }
      },
      error: (res) => {
        if (res.status === 422) {
          AbstractEntityEditComponent.bindErrorsToForm(res.error, this.form);
        }
        this.processing = false;
      },
    });
  }

  updateNote(noteId: number) {
    this.editNoteId = noteId;
    this.showNoteForm = true;
    const note = this.user.notes.find((note) => note.id === noteId);
    if (note) {
      this.noteForm.patchValue({
        note: note.note,
      });
    }
  }

  saveNote() {
    if (!this.noteForm) {
      return;
    }
    if (this.noteForm.invalid) {
      return;
    }

    const data = this.noteForm.value as any;
    data.type = 'internal';
    data.noteable_type = 'user';
    data.noteable_id = this.user.id;

    this.processingNote = true;

    // If editNoteId exists, update existing note
    if (this.editNoteId) {
      this.contactsService.updateContactNote(data, this.editNoteId).subscribe({
        next: (response) => {
          // Update the note in the array
          const index = this.user.notes.findIndex((note) => note.id === this.editNoteId);
          if (index !== -1) {
            this.user.notes[index] = response.data;
          }
          this.cancelNote();
          this.processingNote = false;
        },
        error: (res) => {
          if (res.status === 422) {
            AbstractEntityEditComponent.bindErrorsToForm(res.error, this.noteForm);
          }
          this.processingNote = false;
        },
      });
    } else {
      // Create new note
      this.contactsService.createContactNote(data).subscribe({
        next: (response) => {
          if (!this.user.notes) {
            this.user.notes = [];
          }
          this.user.notes.push(response.data);
          this.cancelNote();
          this.processingNote = false;
        },
        error: (res) => {
          if (res.status === 422) {
            AbstractEntityEditComponent.bindErrorsToForm(res.error, this.noteForm);
          }
          this.processingNote = false;
        },
      });
    }
  }

  cancelNote() {
    this.noteForm?.reset();
    this.showNoteForm = false;
    this.editNoteId = null;
  }

  deleteNote(id: any) {
    this.isDeleteModalOpen = true;
    this.deleteNotesId = id;
  }

  confirmDelete() {
    if (this.deleteNotesId) {
      this.contactsService.deleteContactNote(this.deleteNotesId).subscribe({
        next: () => {
          this.user.notes = this.user.notes.filter((note) => note.id !== this.deleteNotesId);
          this.isDeleteModalOpen = false;
          this.deleteNotesId = null;
        },
        error: (res) => {
          console.error('Error deleting note:', res);
        },
      });
    }
  }

  cancel() {
    return this.router.navigate(['/contacts']);
  }

  private loadInitialEvents() {
    this.eventsService.getChoices('', 1, 20).subscribe((response) => {
      let eventOptions = response.options;

      // If we have selected events that might not be in the first page, fetch them specifically
      const selectedEventIds = this.form.get('event_ids')?.value || [];
      if (selectedEventIds.length > 0) {
        // Check if any selected events are missing from the initial load
        const loadedEventIds = eventOptions.map(e => e.value);
        const missingEventIds = selectedEventIds.filter(id => !loadedEventIds.includes(id));

        if (missingEventIds.length > 0) {
          // Fetch the missing selected events individually
          const missingEventRequests = missingEventIds.map(id =>
            this.eventsService.get(id.toString()).pipe(
              map(response => ({
                value: response.data.id,
                label: response.data.name + (response.data.venue ? ' (' + response.data.venue.name + ')' : ''),
              }))
            )
          );

          forkJoin(missingEventRequests).subscribe((missingEventOptions) => {
            // Combine initial events with missing selected ones
            this.initialEventChoices = [...eventOptions, ...missingEventOptions];
          });
          return;
        }
      }

      // No missing events, just use the initial load
      this.initialEventChoices = eventOptions;
    });
  }

  protected getForms(): FormGroup<any>[] {
    const forms: FormGroup<any>[] = [this.form];
    if (this.noteForm) {
      forms.push(this.noteForm);
    }
    return forms;
  }
}
